import { Card, Form } from "react-bootstrap";
import CustomButton from "../../components/CustomButton/CustomButton";
import { useEffect, useRef, useState } from "react";
import WebService from "../../utility/WebService";
import { PencilFill, TrashFill } from "react-bootstrap-icons";
import DeleteModal from "../../components/DeleteModal/DeleteModal";
import AddGallery from "./AddGallery";
import CustomTableSortable from "../../components/CustomTable/CustomTableSortable";
import HelperService from "../../utility/HelperService";


const columns = [
  {
    id: "name",
    label: "Gallery Name",
    minWidth: 170,
  },
  {
    id: "location",
    label: "Location",
    minWidth: 170,
  },
  {
    id: "tagName",
    label: "Category",
    minWidth: 170,
    sortable: true,
  },
  {
    id: "publishedAtFormatted",
    label: "Event Date",
    minWidth: 170,
    sortable: true,
  },
  {
    id: "action",
    label: "Action",
    minWidth: 100,
    align: "center" as const,
  },
];


const Gallery = () => {
  const [showAddLeadershipCouncil, setShowAddLeadershipCouncil] = useState(false);
  const [leadershipCouncils, setLeadershipCouncils] = useState<any[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [leadershipCouncilToDelete, setLeadershipCouncilToDelete] = useState<string | null>(null);
  const [leadershipCouncilToEdit, setLeadershipCouncilToEdit] = useState<any | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [search, setSearch] = useState("");
  const searchRef = useRef<string>("");
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [uiSortField, setUiSortField] = useState<string>("createdAtFormatted");
  const [loading, setLoading] = useState<boolean>(false);
  const sortFieldMap: Record<string, string> = { createdAtFormatted: "createdAt", name: "name", location: "location", publishedAtFormatted: "publishedAt", tagName: "tagNameSort" };
  const [tagsList, setTagsList] = useState<any[]>([]);
  const [selectedTag, setSelectedTag] = useState("");
 const [pagePermission, setPagePermission] = useState({
    canView: false,
    canAdd: false,
    canEdit: false,
    canDelete: false,
  });
  useEffect(() => {
    fetchTags();
  }, []);

  useEffect(() => {
    getMediaTags(searchRef.current);
  }, [currentPage, selectedTag, sortField, sortOrder]);

  const fetchTags = async () => {
    try {
      const res: any = await WebService.getAPI({
        action: `categories/get-list/GALLERY`,
        body: null,
      });
      setTagsList(res?.data || []);
    } catch (err) {
      console.error("Error fetching tags:", err);
    }
  };

  const getMediaTags = (keyword = "") => {
    const params = new URLSearchParams();
    params.append("page", currentPage.toString());
    params.append("limit", itemsPerPage.toString());

    // Status filter
    if (search && search !== "ALL") {
      params.append("status", search);
    }

    // Keyword search
    if (keyword.trim()) {
      params.append("keyword", keyword.trim());
    }

    // Category filter
    if (selectedTag) {
      params.append("tag", selectedTag);
    }

    // Sorting
    params.append("sortField", sortField);
    params.append("sortOrder", sortOrder);
    WebService.getAPI({
      action: `gallery/get-list?${params.toString()}`,
    })
      .then((res: any) => {
            const perm = res?.data?.permissions || {};

        setPagePermission({
          canView: perm.canView,
          canAdd: perm.canAdd,
          canEdit: perm.canEdit,
          canDelete: perm.canDelete,
        });
        const formattedRows = res?.data?.data.map((item: any) => {
          // ✅ strip HTML
          const stripHtml = (html: string) => {
            if (!html) return "";
            const doc = new DOMParser().parseFromString(html, "text/html");
            return doc.body.textContent || "";
          };

          // ✅ truncate to N words
          const truncateDescription = (text: string, wordLimit = 4) => {
            if (!text) return "";
            const words = text.split(/\s+/);
            return words.length > wordLimit
              ? words.slice(0, wordLimit).join(" ") + "..."
              : text;
          };

          const plainTextDescription = stripHtml(item.description);
          const truncated = truncateDescription(plainTextDescription, 4);

          return {
            ...item,
            tagName: item.tagName || "-",
            publishedAtFormatted: HelperService.getFormatedDate(item.publishedAt) || "-",
            action: (
              <div>
                 {perm.canEdit && (
                <PencilFill
                  className="me-3 cursor-pointer"
                  onClick={() => {
                    setShowAddLeadershipCouncil(true);
                    setLeadershipCouncilToEdit(item);
                  }}
                  title="Edit"
                /> )}
                 {perm.canDelete && (
                <TrashFill
                  color="#DC143C"
                  className="cursor-pointer"
                  onClick={() => handleDeleteEvent(item._id)}
                  title="Delete"
                /> )}
              </div>
            ),
          };
        });
        setLeadershipCouncils(formattedRows);
        setTotalCount(res?.data?.pagination?.total);
      })
      .catch((err: any) => { });
  };

  const handleDeleteEvent = (leadershipCouncilId: string) => {
    setLeadershipCouncilToDelete(leadershipCouncilId);
    setShowDeleteModal(true);
  };

  return (
    <>
      <AddGallery
        show={showAddLeadershipCouncil}
        handleClose={() => {
          setShowAddLeadershipCouncil(false);
          setLeadershipCouncilToEdit(null);
          getMediaTags(searchRef.current);
        }}
        data={leadershipCouncilToEdit}
      />
      <DeleteModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onDelete={() => {
          if (leadershipCouncilToDelete) {
            WebService.deleteAPI({
              action: `gallery/delete-gallery/${leadershipCouncilToDelete}`,
            }).then(() => {
              setShowDeleteModal(false);
              getMediaTags(searchRef.current);
            });
          }
        }}
        message="Are you sure you want to delete this gallery?"
      />
      <div className="events-main">
        <Card className="event-card">
          <div className="d-flex justify-content-between">
            <div className="page-title">Gallery</div>
             {pagePermission.canAdd && (
            <CustomButton
              isLoading={false}
              label="Add"
              onClick={() => {
                setLeadershipCouncilToEdit(null);
                setShowAddLeadershipCouncil(true);
              }}
              style={{ width: 100 }}
            /> )}
          </div>
          <div className="row">
            <div className="col-md-3">
              <Form.Label className="label">Search</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter name or location"
                value={search}
                onChange={(e) => {
                  const value = e.target.value;
                  searchRef.current = value;                  
                  setSearch(value);
                  setCurrentPage(1);
                  getMediaTags(searchRef.current);
                }}
              />
            </div>
            <div className="col-md-3">
              <Form.Label className="label">Category</Form.Label>
              <Form.Select
                value={selectedTag}
                onChange={(e) => {
                  setSelectedTag(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">All Categories</option>
                {tagsList.map((tag: any) => (
                  <option key={tag._id} value={tag._id}>
                    {tag.name}
                  </option>
                ))}
              </Form.Select>
            </div>
          </div>
          <CustomTableSortable
            columns={columns}
            rows={leadershipCouncils}
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={itemsPerPage}
            onPageChange={(page) => {
              setCurrentPage(page);
            }}
            isLoading={loading}
            sortField={uiSortField}
            sortOrder={sortOrder}
            onSortChange={(columnId, order) => {
              setUiSortField(columnId);
              setSortOrder(order);
              const apiField = sortFieldMap[columnId] || "createdAt";
              setSortField(apiField);
              setCurrentPage(1);
              // Fetch is driven by the useEffect (now depends on sortField/sortOrder) using fresh state.
            }}
          />
        </Card>
      </div>
    </>
  );
};

export default Gallery;
